import React from "react";
import { Box, Grid, Typography, useTheme, styled, Paper } from "@mui/material";
import { AccessTime, LocalDining, Person, Schedule } from "@mui/icons-material";
import PropTypes from "prop-types";

const TimeCardContainer = styled(Paper)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(3.5),
  borderRadius: theme.shape.borderRadius * 3,
  backgroundColor: theme.palette.background.paper,
  transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
  boxShadow: "0 6px 20px rgba(0, 0, 0, 0.05)",
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 12px 28px rgba(0, 0, 0, 0.1)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "6px",
    background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
    opacity: 0.8,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2.5),
    "&:hover": {
      transform: "translateY(-4px)",
    },
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: "50%",
  background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.primary.contrastText,
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  marginBottom: theme.spacing(2),
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "scale(1.1) rotate(5deg)",
  },
}));

const TimeCard = ({ label, value, icon }) => {
  const theme = useTheme();
  return (
    <Grid item xs={12} sm={6} md={3}>
      <TimeCardContainer elevation={0}>
        <IconContainer>
          {React.cloneElement(icon, { fontSize: "medium" })}
        </IconContainer>

        <Typography
          variant="h6"
          color="text.secondary"
          fontWeight={500}
          sx={{
            mb: 1,
            textAlign: "center",
            letterSpacing: "0.5px",
          }}
        >
          {label}
        </Typography>

        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            color: theme.palette.text.primary,
            position: "relative",
            textAlign: "center",
            mb: 1,
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -8,
              left: "50%",
              transform: "translateX(-50%)",
              width: "40%",
              height: "3px",
              background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
              borderRadius: "4px",
            },
          }}
        >
          {value}
        </Typography>
      </TimeCardContainer>
    </Grid>
  );
};

const CookingTimes = ({ prepTime, cookTime, serving }) => {
  // Convert seconds to minutes and handle null/undefined values
  const prepMinutes = Math.max(Math.floor((prepTime || 0) / 60), 0);
  const cookMinutes = Math.max(Math.floor((cookTime || 0) / 60), 0);
  const totalMinutes = prepMinutes + cookMinutes;

  // Remove console.log for production
  // console.log(prepTime, cookTime, serving);

  return (
    <Box
      sx={{
        mb: 6,
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: -20,
          left: "50%",
          transform: "translateX(-50%)",
          width: "80%",
          height: "1px",
          backgroundColor: "divider",
          opacity: 0.6,
        },
      }}
    >
      <Grid container spacing={3}>
        <TimeCard
          label="Prep Time"
          value={`${prepMinutes}m`}
          icon={<AccessTime />}
        />

        <TimeCard
          label="Cook Time"
          value={`${cookMinutes}m`}
          icon={<LocalDining />}
        />

        <TimeCard
          label="Total Time"
          value={`${totalMinutes}m`}
          icon={<Schedule />}
        />

        <TimeCard label="Servings" value={serving || 4} icon={<Person />} />
      </Grid>
    </Box>
  );
};

CookingTimes.propTypes = {
  prepTime: PropTypes.number,
  cookTime: PropTypes.number,
  serving: PropTypes.number,
};

TimeCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.element.isRequired,
};

export default CookingTimes;
